<template>
  <custom-dialog
    id="updateCEModuleDialog"
    v-model="popupDialog"
    body-id="updateCEModuleDialogBody"
    fullscreen
    heading="Module Update Summary"
    subheading=""
    :primary-action="{
      id: 'content-update-ce-module-dialog--update-btn',
      label: 'Update',
      disabled: loading
    }"
    :secondary-action1="{ id: 'content-update-ce-module-dialog--cancel-btn', label: 'Cancel' }"
    @primary-click="handlePrimaryClick"
    @secondary1-click="popupDialog = false"
  >
    <template #custom-dialog-content>
      <div
        v-if="
          fileDiff &&
          ((fileDiff.added && fileDiff.added.length) ||
            (fileDiff.deleted && fileDiff.deleted.length) ||
            (fileDiff.modified && fileDiff.modified.length))
        "
        class="html-difference"
      >
        <div class="update-ce-module-dialog-content">
          <hx-content-card height="100%" class="update-ce-module-dialog-toc">
            <template #header>
              <div class="font-weight-bold">
                {{
                  moduleData.displayName ? substituteContextKeyValue(moduleData.displayName) : ''
                }}
              </div>
            </template>

            <div class="module-differences-toc mt-2">
              <v-treeview
                :items="moduleData.toc"
                item-title="name"
                item-key="id"
                item-value="id"
                expand-icon="mdi-menu-right"
                collapse-icon="mdi-menu-down"
                density="compact"
                tile
                :opened="open"
              >
                <template #title="{ item }">
                  <div
                    :id="getNodeId(item)"
                    :class="[
                      'diff-toc-page',
                      'text-link',
                      'text-truncate',
                      getPageDiffStatusClass(item.updateStatus)
                    ]"
                    :title="item && item.name ? substituteContextKeyValue(item.name) : ''"
                    @click="item.updateStatus && showPageDifferences(item)"
                  >
                    {{ item && item.name ? substituteContextKeyValue(item.name) : '' }}
                  </div>
                </template>
              </v-treeview>
            </div>
          </hx-content-card>
          <hx-content-card v-if="selectedPage" max-height="100%">
            <template #header>
              <div class="font-weight-bold">Page Differences</div>
            </template>
            <v-col id="content-update-ce-module-dialog--page-differences">
              <v-row>
                <v-col class="updates-key export-differences-legend">
                  <v-icon icon="mdi-information-outline" class="information-outline" />
                  <h3>Key to Highlights and Changes</h3>
                  <v-tooltip
                    v-for="item in legendItems"
                    :id="item.id"
                    :key="item.id"
                    location="bottom"
                  >
                    <template #activator="{ props }">
                      <div :class="item.class" v-bind="props"></div>
                    </template>
                    <span>
                      In the <i>Table of Contents</i>: <span v-html="item.tocText"></span>.<br />
                      In the <i>Page Content</i>: <span v-html="item.contentText"></span>.
                    </span>
                  </v-tooltip>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <div class="d-flex flex-row align-center justify-space-between">
                    <span
                      >The following changes have been made to this page with this module
                      update.</span
                    >
                    <span>
                      <cs-button
                        id="content-update-ce-module-dialog--return-to-module-diff-summary-btn"
                        class="mb-4 mr-2"
                        label="Return to Update Summary"
                        primary
                        large
                        @click="selectedPage = null"
                      ></cs-button>
                      <cs-button
                        id="content-update-ce-module-dialog--export-page-differences-btn"
                        class="mb-4"
                        label="Export Differences"
                        primary
                        large
                        @click="exportDifferences"
                      ></cs-button>
                    </span>
                  </div>
                  <v-alert
                    v-if="
                      selectedPage &&
                      selectedPage.currentContent &&
                      selectedPage.updatedContent &&
                      !selectedPage.updatedContent.includes('vdd-added') &&
                      !selectedPage.updatedContent.includes('vdd-removed') &&
                      !selectedPage.updatedContent.includes('vdd-modified') &&
                      selectedPage.updatedContent !== '<div>No content</div>'
                    "
                    id="content-update-ce-module-dialog--changes-do-not-apply-message"
                    class="mt-2"
                    width="100%"
                    type="info"
                  >
                    This content has been changed, but does not apply to your site.
                  </v-alert>

                  <h2 class="mt-4">
                    {{
                      selectedPage && selectedPage.name
                        ? substituteContextKeyValue(selectedPage.name)
                        : ''
                    }}
                  </h2>
                </v-col>
              </v-row>
              <v-row>
                <table class="differences-table">
                  <thead>
                    <tr>
                      <th>Current User View Content</th>
                      <th>Updated User View Content</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div v-html="selectedPage.currentContent"></div>
                      </td>
                      <td>
                        <div v-html="selectedPage.updatedContent"></div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </v-row>
            </v-col>
          </hx-content-card>
          <hx-content-card v-if="!selectedPage" max-height="100%">
            <v-col class="module-update-summary">
              <v-row>
                <v-col class="updates-key">
                  <v-icon icon="mdi-information-outline" class="information-outline" />
                  <h3>Key to Highlights and Changes</h3>
                  <v-tooltip
                    v-for="item in legendItems"
                    :id="item.id"
                    :key="item.id"
                    location="bottom"
                  >
                    <template #activator="{ props }">
                      <div :class="item.class" v-bind="props"></div>
                    </template>
                    <span>
                      In the <i>Table of Contents</i>: <span v-html="item.tocText"></span>.<br />
                      In the <i>Page Content</i>: <span v-html="item.contentText"></span>.
                    </span>
                  </v-tooltip>
                </v-col>
              </v-row>
              <v-row v-if="fileDiff && fileDiff.added && fileDiff.added.length">
                <v-col>
                  <div class="d-flex flex-nowrap">
                    <h2 class="mr-2">Added Pages</h2>
                    <v-tooltip location="right">
                      <template #activator="{ props }">
                        <v-icon
                          icon="mdi-information-outline"
                          class="information-outline mb-6"
                          v-bind="props"
                        />
                      </template>
                      <span>This lists all page additions that are included in this update.</span>
                    </v-tooltip>
                  </div>

                  <div v-for="addedFile in fileDiff.added" :key="addedFile.fileName">
                    <div class="mb-2">
                      <v-icon icon="mdi-file-document" class="added-page-color mr-2" />
                      <span>{{ substituteContextKeyValue(addedFile.pageName) }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row v-if="fileDiff && fileDiff.deleted && fileDiff.deleted.length">
                <v-col>
                  <div class="d-flex flex-nowrap">
                    <h2 class="mr-2">Deleted Pages</h2>
                    <v-tooltip location="right">
                      <template #activator="{ props }">
                        <v-icon icon="mdi-information-outline" class="mb-6" v-bind="props" />
                      </template>
                      <span>This lists all page deletions that are included in this update.</span>
                    </v-tooltip>
                  </div>
                  <div v-for="deletedFile in fileDiff.deleted" :key="deletedFile.fileName">
                    <div class="mb-2">
                      <v-icon icon="mdi-file-document" class="deleted-page-color mr-2" />
                      <span>{{ substituteContextKeyValue(deletedFile.pageName) }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
              <v-row v-if="fileDiff && fileDiff.modified && fileDiff.modified.length">
                <v-col>
                  <div class="d-flex flex-nowrap">
                    <h2 class="mr-2">Changed Pages</h2>
                    <v-tooltip location="right">
                      <template #activator="{ props }">
                        <v-icon icon="mdi-information-outline" class="mb-6" v-bind="props" />
                      </template>
                      <span
                        >This section shows all content changes in this update, including formatting
                        changes to existing content, added content and deleted content.</span
                      >
                    </v-tooltip>
                  </div>
                  <div v-for="modifiedFile in fileDiff.modified" :key="modifiedFile.fileName">
                    <div class="mb-2">
                      <v-icon icon="mdi-file-document" class="modified-page-color mr-2" />
                      <span>{{ substituteContextKeyValue(modifiedFile.pageName) }}</span>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>
          </hx-content-card>
        </div>
      </div>

      <cs-form-dialog
        v-model="conflictDialog"
        max-width="500px"
        heading="New Updates"
        :primary-action="{ label: 'OK' }"
        @primary-click="refresh"
      >
        <template #cs-form-dialog-content>
          <v-container>
            <v-row>
              <span>
                New updates have become available since this page was last loaded. This page will
                refresh with newer information once
                <strong>OK</strong> button is clicked.
              </span>
            </v-row>
          </v-container>
        </template>
      </cs-form-dialog>
    </template>
  </custom-dialog>
  <group-publish-dialog v-model="groupPublishDialog" @publish="handleGroupPublish" />
</template>

<script>
import { mapState, mapMutations, mapGetters } from 'vuex';
import moment from 'moment-timezone';
import { CSBase } from '@complispace/cs-design-system';
import CustomDialog from '@/components/custom/CustomDialog';
import * as types from '@/store/mutationTypes';
import dataManager from '@/lib/engine';
import {
  PUBLISH_TYPE_NORMAL,
  PUBLISH_OPTIONS,
  exportPageHTML,
  MIXPANEL_TRACE_EVENT,
  MIXPANEL_TRACE_STRING
} from '@/constants';
import { loadAllImages } from '@/lib/imageRenderUtils';
import updateTableColumnsWidth from '@/lib/tableUtils';
import { getContextKeySubstitutedTitle } from '@/lib/util';
import { plugInAdapter } from '@/lib/globalPlugIns';
import { publish, client } from '@/api';
import EventBus from '@/bus';
import GroupPublishDialog from '@/components/content/GroupPublishDialog';

const GROUP_PUBLISH_FALLBACK_MESSAGE =
  'The update was successfully applied to the parent site only. Please publish the changes to the child sites via the <href>Group Publish</href> page.';

export default {
  name: 'UpdateCEModuleDialog',

  components: {
    CustomDialog,
    GroupPublishDialog
  },
  extends: CSBase,

  props: {
    modelValue: { type: Boolean, required: true, default: false },
    fileDiff: { type: Object, required: true, default: () => {} },
    publishId: { type: Number, required: true },
    moduleId: { type: Number, required: true }
  },

  emits: ['update:modelValue', 'updateParent'],

  data() {
    return {
      conflictDialog: false,
      groupPublishDialog: false,
      moduleData: {},
      selectedPage: null,
      legendItems: [
        {
          id: 'content-update-ce-module-dialog--added-tooltip',
          class: 'legend-item added-page-color',
          tocText: 'Page has been <strong>added</strong>',
          contentText: 'Content has been <strong>added</strong>',
          label: 'Green'
        },
        {
          id: 'content-update-ce-module-dialog--deleted-tooltip',
          class: 'legend-item deleted-page-color',
          tocText: 'Page has been <strong>deleted</strong>',
          contentText: 'Content has been <strong>removed</strong>',
          label: 'Red'
        },
        {
          id: 'content-update-ce-module-dialog--modified-tooltip',
          class: 'legend-item modified-page-color',
          tocText: 'Page has been <strong>changed</strong>',
          contentText: 'Content has been <strong>modified</strong> or <strong>reformatted</strong>',
          label: 'Yellow'
        }
      ]
    };
  },

  computed: {
    ...mapState({
      client: (state) => state.client,
      subdomain: (state) => state.session.subdomain
    }),
    ...mapGetters({
      appliedUpdateItems: 'published/appliedItems',
      activatedCEModules: 'activatedCEModules/items',
      contextValues: 'clientContext/values',
      appName: 'app/name'
    }),
    isGroupParent() {
      if (this.client.children && this.client.children.length > 0) {
        return true;
      }
      return false;
    },
    popupDialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        if (value === this.modelValue) return;
        this.$emit('update:modelValue', value);
      }
    },
    open() {
      if (this.moduleData.toc && this.moduleData.toc.length) {
        return this.moduleData.toc.map((item) => item.id);
      }
      return [];
    }
  },

  watch: {
    popupDialog: {
      async handler(value) {
        if (value) {
          this.conflictDialog = false; // reset conflict dialog
          await this.getModuleData();
        }
      },
      immediate: true
    }
  },

  methods: {
    ...mapMutations({
      setPageCommitOID: types.DATA_SET_PAGE_COMMIT_OID,
      setPageCommitTimestamp: types.DATA_SET_PAGE_COMMIT_TIMESTAMP,
      setDataUpdatedModulesState: types.DATA_SET_UNPUBLISHED_UPDATES_STATE
    }),

    async handleGroupPublish(publishOption) {
      if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_ONLY) {
        await this.update(publishOption);
      } else if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_AND_CHILD_SITES) {
        const updateResult = await this.update(publishOption);
        if (updateResult && updateResult.success) {
          await this.notifyChildSites(this.moduleId);
        }
      } else if (publishOption === PUBLISH_OPTIONS.ALL_SITES) {
        const updateResult = await this.update(publishOption);
        if (updateResult && updateResult.success) {
          await this.publishAndAcceptChildSites(this.moduleId);
        }
      }
    },

    async handlePrimaryClick() {
      if (this.isGroupParent) {
        this.groupPublishDialog = true;
      } else {
        await this.update(PUBLISH_OPTIONS.PARENT_SITE_ONLY);
      }
    },
    async update(publishOption) {
      if (this.loading) return;
      this.setLoading(true);
      this.clearAlert();

      const moduleUpdateItems = [];
      if (!!this.appliedUpdateItems && this.appliedUpdateItems.length > 0) {
        this.appliedUpdateItems.forEach((update) => {
          if (update.moduleId === this.moduleId) {
            const foundModule = this.activatedCEModules.find(
              (activatedCEModules) => activatedCEModules.id === update.moduleId
            );
            // check if clientId where CS module = null or undefined, include
            if (
              !!foundModule &&
              (foundModule.clientId === null || foundModule.clientId === undefined)
            ) {
              moduleUpdateItems.push(update);
            }
          }
        });
      }
      try {
        const response = await publish.merge(this.publishId, this.moduleId);

        const { hasConflicts } = response.data;
        // Fetch the context keys again - there might be new ones
        // from the update
        if (!hasConflicts) {
          await this.$store.dispatch('clientContext/getAll');

          this.trackMixPanelAcceptedUpdatesEvent(moduleUpdateItems);

          // TODO: the following may be removed in the future with further verification
          // $emit('updateParent') will also call the following two

          await this.$store.dispatch('published/getAllAppliedUpdates');

          await this.$store.dispatch('published/getAllComplispaceUpdates');

          // Reset pageCommitOID and pageCommitTimestamp, which are used to indicate that we are viewing a previous version of content corresponding to the commit OID.
          // When we accept the latest update, we should only see the latest content version with latest context key values.
          this.setPageCommitOID('');
          this.setPageCommitTimestamp('');
          this.setDataUpdatedModulesState(dataManager.MANAGER_STATE.STATE_IDLE);

          // Only show success alert for parent-site-only option
          // For other options, the calling function will handle the message
          if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_ONLY) {
            this.showSuccessAlert('Update has been successful.');
          }
        }

        this.$emit('updateParent');
        EventBus.$emit('updateSideNavigation');

        // Only close dialog for parent-site-only option or no option specified
        // For other options, keep dialog open until the full process completes
        if (publishOption === PUBLISH_OPTIONS.PARENT_SITE_ONLY) {
          this.popupDialog = hasConflicts;
        }
        return { success: true };
      } catch (err) {
        if (err.response && err.response.status === 409) {
          this.conflictDialog = true;
        } else {
          const statusMessage = err.status ? `(status code: ${err.status})` : '';
          const errorMessage = `Update has been unsuccessful, please try again. ${statusMessage}`;
          this.showErrorAlert(errorMessage, true);
        }
        return { success: false };
      } finally {
        this.setLoading(false);
      }
    },
    trackMixPanelAcceptedUpdatesEvent(moduleUpdateItems) {
      try {
        if (!!moduleUpdateItems && moduleUpdateItems.length > 0) {
          // eslint-disable-next-line no-underscore-dangle
          const { plugIns } = this.$app;

          const currentDate = moment(new Date()).format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]');

          const commonMixpanelProperties = {
            appName: this.appName,
            client: this.client,
            subdomain: this.subdomain,
            userRole: MIXPANEL_TRACE_STRING.USER_ROLE_VIEW.ADMIN
          };

          moduleUpdateItems.forEach((update) => {
            if (update.typeId === PUBLISH_TYPE_NORMAL) {
              const pendingUpdatesProperties = {
                publishId: update.publishId,
                publishSummary: update.publishSummary,
                typeId: update.typeId,
                publishAt: currentDate,
                moduleId: update.moduleId,
                moduleName: update.moduleName
              };

              const properties = plugInAdapter.Adapter.getCustomMixpanelProperties({
                ...commonMixpanelProperties,
                ...pendingUpdatesProperties
              });

              plugIns.mixPanelTrackEvent(MIXPANEL_TRACE_EVENT.ACCEPTED_UPDATES, properties);
            }
          });
        }
      } catch (error) {
        console.log('Mixpanel Error: ', error);
      }
    },

    refresh() {
      window.location.reload();
    },

    async getModuleData() {
      let activatedModule = null;

      if (this.activatedCEModules && this.activatedCEModules.length) {
        activatedModule = this.activatedCEModules.find((module) => module.id === this.moduleId);
      }

      const formattedToc = this.updateTocJsonProperties(this.fileDiff.mergedToc);
      this.moduleData = { ...activatedModule, toc: [formattedToc] };
    },

    updateTocJsonProperties(toc) {
      if (this.fileDiff.added.find((addedPage) => addedPage.fileName === toc.path)) {
        // eslint-disable-next-line no-param-reassign
        toc.updateStatus = 'added';
      }

      if (this.fileDiff.deleted.find((deletedPage) => deletedPage.fileName === toc.path)) {
        // eslint-disable-next-line no-param-reassign
        toc.updateStatus = 'deleted';
      }

      if (this.fileDiff.modified.find((modifiedPage) => modifiedPage.fileName === toc.path)) {
        // eslint-disable-next-line no-param-reassign
        toc.updateStatus = 'modified';
      }

      if (toc.children && toc.children.length) {
        toc.children.forEach((child) => {
          this.updateTocJsonProperties(child);
        });
      } else {
        // eslint-disable-next-line no-param-reassign
        toc.children = undefined; // add else block to set toc.children as undefined instead of leaving it as empty array if has no children
      }
      return toc;
    },

    getNodeId(item) {
      const path = item && item.path ? item.path.substring(0, item.path.length - 3) : '';
      return `toc-node-${this.moduleId}-${path}`;
    },

    getPageDiffStatusClass(status) {
      let diffClass;
      switch (status) {
        case 'added':
          diffClass = 'added-page';
          break;

        case 'deleted':
          diffClass = 'deleted-page';
          break;

        case 'modified':
          diffClass = 'modified-page';
          break;

        default:
          diffClass = '';
          break;
      }
      return diffClass;
    },

    async showPageDifferences(item) {
      try {
        const response = await publish.getPageDiff(this.publishId, item.path);
        const currentContent =
          response.status === 200 && response.data && response.data.originalContent.length
            ? updateTableColumnsWidth(response.data.originalContent)
            : '<div>No content</div>';

        let updatedContent = '<div>No content</div>';

        if (
          response.status === 200 &&
          response.data &&
          response.data.differences.length &&
          item.updateStatus !== 'deleted'
        ) {
          updatedContent = updateTableColumnsWidth(response.data.differences);
        }

        this.selectedPage = { ...item, currentContent, updatedContent };
      } catch (err) {
        this.selectedPage = {
          ...item,
          currentContent: '<div>No content</div>',
          updatedContent: '<div>No content</div>'
        };
        this.showErrorAlert(err.message);
      }
    },

    substituteContextKeyValue(text) {
      return getContextKeySubstitutedTitle(text, this.contextValues);
    },

    async exportDifferences() {
      const differences = document.getElementById(
        'content-update-ce-module-dialog--page-differences'
      );

      const filename = this.selectedPage.name || 'Print View';
      const winPrint = window.open('', filename, 'toolbar=0,titlebar=1,scrollbars=1,status=0');
      moment.tz.setDefault('Australia/Sydney');
      const currentDate = moment(new Date()).format('DD/MM/YYYY HH:mm');

      const legendHTML = `
          <h1>Page Differences</h1>
          <div class="updates-key">
            <h3>Key to Highlights and Changes</h3>
            <ul>
              ${this.legendItems
                .map(
                  (item) => `
                <li style="display: flex;">
                  <div class="${item.class}" style="font-size: 48px; line-height: 0.5; margin-right: 8px;">■</div>
                  <div><b>${item.label}</b>: ${item.contentText}</div>
                </li>
              `
                )
                .join('')}
            </ul>
          </div>
        `;

      const processedHTML = differences.innerHTML;

      const contentWithLegend = processedHTML.replace(
        /<div[^>]*class="[^"]*updates-key[^"]*export-differences-legend[^"]*"[^>]*>[\s\S]*?<\/div>/g,
        legendHTML
      );

      const exportedHTML = exportPageHTML(
        currentDate,
        currentDate,
        filename,
        this.client.logoUri,
        contentWithLegend
      );
      winPrint.document.write(exportedHTML);
      await loadAllImages(winPrint.document);
      winPrint.document.close();
    },

    closeDialog() {
      this.selectedPage = null;
      this.popupDialog = false;
    },

    async notifyChildSites(moduleId) {
      try {
        this.setLoading(true);
        const response = await client.notify(false, [moduleId]);
        if (response.status === 201) {
          const message = 'The Group Publish for the selected module was successful.';
          this.showSuccessAlert(message);
        } else if (response.status === 202) {
          const message = 'Please apply module updates before notifying your child clients.';
          this.showWarningAlert(message);
        } else if (response.status === 203) {
          const message = 'There were no module update notifications to send.';
          this.showWarningAlert(message);
        }
      } catch (error) {
        this.showWarningAlert(GROUP_PUBLISH_FALLBACK_MESSAGE, false, {
          href: '/preferences/groupAdmin/group-publish'
        });
      } finally {
        this.popupDialog = false;
        this.setLoading(false);
      }
    },
    async publishAndAcceptChildSites(moduleId) {
      try {
        this.setLoading(true);
        const response = await client.publishUpdateChildren([moduleId]);
        if (response.status === 200 && response.data[0].status !== 'ERROR') {
          const message =
            'Your update was successful and you may continue with other tasks in the meantime. To monitor the progress, check the <href>Processing Queue</href>.';
          this.showSuccessAlert(message, false, {
            href: '/preferences/groupAdmin/processing-queue'
          });
        } else {
          this.showWarningAlert(GROUP_PUBLISH_FALLBACK_MESSAGE, false, {
            href: '/preferences/groupAdmin/group-publish'
          });
        }
      } catch (error) {
        this.showWarningAlert(GROUP_PUBLISH_FALLBACK_MESSAGE, false, {
          href: '/preferences/groupAdmin/group-publish'
        });
      } finally {
        this.popupDialog = false;
        this.setLoading(false);
      }
    }
  }
};
</script>

<style scoped>
.html-difference :deep(.vdd-added) {
  background-color: #c8e6c9;
}

.html-difference :deep(.vdd-removed) {
  background-color: #ffcdd2;
}

.html-difference :deep(.vdd-modified) {
  background-color: #ffecb3;
}

.html-difference :deep(ins),
.html-difference :deep(del) {
  text-decoration: none;
}

.html-difference :deep(.v-card__text div > h1:first-child),
.html-difference :deep(.v-card__text div > h2:first-child),
.html-difference :deep(.v-card__text div > h3:first-child),
.html-difference :deep(.v-card__text div > h4:first-child),
.html-difference :deep(.v-card__text div > h5:first-child),
.html-difference :deep(.v-card__text div > h6:first-child) {
  font-size: 24px;
  font-weight: 400;
  color: #555757;
  line-height: 32px;
  letter-spacing: normal;
  margin-bottom: 2rem;
}

.html-difference :deep(h1),
.html-difference :deep(h2),
.html-difference :deep(h3) {
  margin-bottom: 1.5rem;
  color: rgba(0, 0, 0, 0.6);
}

.html-difference :deep(.v-btn__content .v-icon),
.html-difference :deep(.information-outline) {
  color: rgba(0, 0, 0, 0.6) !important;
}

.html-difference :deep(h4),
.html-difference :deep(h5),
.html-difference :deep(h6) {
  margin-bottom: 1rem;
}
.html-difference :deep(span.underline) {
  text-decoration: underline !important;
}

.html-difference :deep(.pm-table-wrapper) {
  overflow: auto;
}

.html-difference :deep(table) {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin-bottom: 1rem;
}

/* Legacy tables saved with an absolute pixel width on the table element itself must still be
   constrained to the container instead of overflowing it; per-cell widths are left alone so
   relative column proportions are preserved. */
.html-difference :deep(table[style]) {
  max-width: 100% !important;
  min-width: 0 !important;
}

.html-difference :deep(td),
div :deep(th) {
  border: 1px solid #dddddd;
  padding: 8px;
  vertical-align: top;
}

.html-difference :deep(.pm-table-wrapper td),
.html-difference :deep(.pm-table-wrapper th) {
  word-break: break-word;
}

.html-difference :deep(th) {
  text-align: left;
  background-color: #f2f2f2;
  color: black;
}

.html-difference :deep(img) {
  display: block;
  margin: 0 auto;
  max-width: 80%;
  width: auto !important;
  height: auto !important;
}

.html-difference :deep(pre) {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;
}

.html-difference :deep(pre code) {
  display: block;
}

.v-application .html-difference :deep(code) {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
}

.html-difference :deep(blockquote) {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  padding-left: 0.8rem;
  font-style: italic;
}

.html-difference :deep(ol ol) {
  list-style-type: lower-alpha;
}

.html-difference :deep(ol ol ol) {
  list-style-type: lower-roman;
}

.html-difference :deep(ul),
.html-difference :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 2rem !important;
}

.html-difference :deep(li p) {
  margin: 0;
}

.html-difference li > ul,
.html-difference li > ol {
  margin-bottom: auto;
}

.module-differences-toc :deep(.added-page) {
  background-color: #c8e6c9;
  cursor: pointer !important;
}

.module-differences-toc :deep(.deleted-page) {
  background-color: #ffcdd2;
  cursor: pointer !important;
}

.module-differences-toc :deep(.modified-page) {
  background-color: #ffecb3;
  cursor: pointer !important;
}

.module-update-summary :deep(.added-page-color),
.html-difference :deep(.added-page-color) {
  color: #c8e6c9;
  background-color: #c8e6c9;
}

.module-update-summary :deep(.deleted-page-color),
.html-difference :deep(.deleted-page-color) {
  color: #ffcdd2;
  background-color: #ffcdd2;
}

.module-update-summary :deep(.modified-page-color),
.html-difference :deep(.modified-page-color) {
  color: #ffecb3;
  background-color: #ffecb3;
}

.module-differences-toc :deep(.diff-toc-page) {
  padding: 0.5rem;
  border-radius: 5px;
  cursor: default;
}

.module-update-summary .updates-key,
.html-difference .updates-key {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.html-difference table.differences-table th {
  width: 50%;
}

.module-update-summary .updates-key h3,
.html-difference .updates-key h3 {
  margin-bottom: 0;
}

/* fix indentation limitation on vuetify treeview - https://github.com/vuetifyjs/vuetify/issues/20421 */
.html-difference
  :deep(
    .v-list-item.v-list-item--link.v-list-item--density-compact.v-treeview-item:not(
        .v-list-group__header
      )
  ) {
  padding-inline-start: calc(44px + var(--indent-padding)) !important;
}

.legend-item {
  height: 2rem;
  width: 3rem;
  border-radius: 0.5rem;
}

.update-ce-module-dialog-content {
  display: grid;
  grid-template-columns: 1fr 3fr;
  gap: 1rem;
  height: 80vh;
}

.update-ce-module-dialog-toc {
  border: none !important;
}
</style>
